import { Box, Typography } from '@mui/material'
import {
  DataGrid,
  type GridFilterModel,
  type GridRowSelectionModel,
  type GridSortModel,
  useGridApiRef,
} from '@mui/x-data-grid'
import { useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo, useState } from 'react'
import ExportCSV from '../../components/ExportCSV'
import NoTableResults from '../../components/NoTableResults'
import { ReleasePaymentButton } from '../../components/ReleasePaymentButton'
import RequeuePaymentButton from '../../components/RequeuePaymentButton'
import { StartReviewButton } from '../../components/StartReviewButton'
import { type ListPaymentsQueryVariables, type Payment, useListPaymentsQuery } from '../../generated/graphql'
import { parseMuiFilterModel } from '../../utils/filterUtils'
import { formatStatus } from '../../utils/util'
import { getColumns } from './Columns'

export default function Payments() {
  const { status: statusParam } = useParams({
    from: '/payments/status/$status',
  })
  const navigate = useNavigate({ from: '/payments/status/$status' })
  const searchParams = useSearch({ from: '/payments/status/$status' })
  const dataGridApiRef = useGridApiRef()

  const [paginationModel, setPaginationModel] = useState(() => ({
    page: typeof searchParams?.page === 'number' ? searchParams.page : 0,
    pageSize: typeof searchParams?.pageSize === 'number' ? searchParams.pageSize : 250,
  }))

  const [sortModel, setSortModel] = useState<GridSortModel>(() => {
    const sortParam = searchParams?.sort
    if (typeof sortParam === 'string') {
      try {
        const decoded = JSON.parse(atob(sortParam))
        return decoded as GridSortModel
      } catch {
        return [{ field: 'id', sort: 'desc' }]
      }
    }
    return [{ field: 'id', sort: 'desc' }]
  })

  const [filterModel, setFilterModel] = useState<GridFilterModel>(() => {
    const filterParam = searchParams?.filter
    if (typeof filterParam === 'string') {
      try {
        const decoded = JSON.parse(atob(filterParam))
        return decoded as GridFilterModel
      } catch {
        return { items: [] }
      }
    }
    return { items: [] }
  })

  // Handle special status values for failed subcategories
  const rawStatus = statusParam ? statusParam.toUpperCase() : 'UNDER_REVIEW'
  const isFailedApi = rawStatus === 'FAILED_API'
  const isFailedBank = rawStatus === 'FAILED_BANK'
  const isFailedUnknown = rawStatus === 'FAILED_UNKNOWN'
  const status = isFailedApi || isFailedBank || isFailedUnknown ? 'FAILED' : rawStatus

  // const offset =

  const sortArray = useMemo(
    () =>
      sortModel.length
        ? sortModel
            .map(({ field, sort }) => ({
              columnField: field,
              sort: sort as 'asc' | 'desc',
            }))
            .filter(s => s.sort)
        : undefined,
    [sortModel]
  )

  const filterArray = useMemo(() => {
    const filters = []

    // Handle special failed subcategories
    if (isFailedApi) {
      filters.push({
        columnField: 'isBankError',
        operatorValue: '=',
        stringValue: 'false',
      })
    }

    if (isFailedBank) {
      filters.push({
        columnField: 'isBankError',
        operatorValue: '=',
        stringValue: 'true',
      })
    }

    if (isFailedUnknown) {
      filters.push({
        columnField: 'isBankError',
        operatorValue: 'isEmpty',
      })
    }

    if (filterModel?.items.length) {
      const validItems = filterModel.items
        .filter(({ field, operator, value }) => {
          if (!field || !operator) return false
          if (operator !== 'isEmpty' && operator !== 'isNotEmpty' && !value) return false
          return true
        })
        .map(({ field, operator, value }) => ({
          columnField: field,
          operatorValue: operator,
          value: value,
        }))

      const parsedFilters = parseMuiFilterModel({ items: validItems })
      if (parsedFilters) {
        filters.push(...parsedFilters)
      }
    }

    return filters.length > 0 ? filters : undefined
  }, [filterModel, isFailedApi, isFailedBank, isFailedUnknown])

  const queryVariables = useMemo(
    () => ({
      status: [status],
      limit: paginationModel.pageSize,
      offset: paginationModel.page * paginationModel.pageSize,
      sort: sortArray,
      filters: filterArray,
    }),
    [status, paginationModel, sortArray, filterArray]
  ) as ListPaymentsQueryVariables

  const {
    data,
    isLoading: isPaymentsLoading,
    isFetching: isPaymentsFetching,
    refetch,
  } = useListPaymentsQuery(queryVariables, {
    placeholderData: (previousData, previousQuery) => {
      const currentStatus = queryVariables?.status ? [...queryVariables.status].sort().join(',') : 'all'
      const previousStatuses = (previousQuery?.queryKey[1] as ListPaymentsQueryVariables)?.status
      const previousStatus = previousStatuses ? [...previousStatuses].sort().join(',') : undefined

      if (currentStatus === previousStatus) {
        return previousData
      }
      return undefined
    },
  })

  const payments: Payment[] = (data?.listPayments?.payments || []) as Payment[]
  const paymentsCount: number = data?.listPayments?.count || 0

  useEffect(() => {
    const newSearch: Record<string, unknown> = {
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
      sort: sortModel.length > 0 ? btoa(JSON.stringify(sortModel)) : undefined,
      filter: filterModel.items.length > 0 ? btoa(JSON.stringify(filterModel)) : undefined,
    }

    navigate({
      search: newSearch,
      replace: true,
    })
  }, [paginationModel.page, paginationModel.pageSize, sortModel, filterModel, navigate])

  // if current page is beyond available data, go to last valid page
  useEffect(() => {
    if (paymentsCount > 0) {
      const maxPage = Math.ceil(paymentsCount / paginationModel.pageSize) - 1
      if (paginationModel.page > maxPage) {
        setPaginationModel(prev => ({ ...prev, page: maxPage }))
      }
    }
  }, [paymentsCount, paginationModel.page, paginationModel.pageSize])

  const [selectedPaymentIds, setSelectedPaymentIds] = useState<number[]>([])

  const handleRowSelectionModelChange = useCallback(
    (selectionModel: GridRowSelectionModel) => {
      setSelectedPaymentIds(Array.from(dataGridApiRef.current?.state?.rowSelection?.ids as Set<number>))
      if (selectionModel.type === 'include') {
        setSelectedPaymentIds(Array.from(selectionModel.ids as Set<number>))
        return
      }

      // exclude mode: return all payment IDs except the excluded ones
      const allIds = payments.map(p => p.id as number)
      const excludedIds = selectionModel.ids as Set<number>
      setSelectedPaymentIds(allIds.filter(id => !excludedIds.has(id)))
    },
    [payments, dataGridApiRef.current]
  )

  const getPageTitle = () => {
    if (isFailedBank) {
      return 'Payments: FAILED - Bank Errors'
    }
    if (isFailedApi) {
      return 'Payments: FAILED - API Failures'
    }
    if (isFailedUnknown) {
      return 'Payments: FAILED - Unknown'
    }
    return `Payments: ${formatStatus(status)}`
  }

  return (
    <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h5">{getPageTitle()}</Typography>
        <ExportCSV
          ref={dataGridApiRef}
          disabled={payments.length === 0}
          fileName={`payments-${rawStatus.toLowerCase()}`}
        />
      </Box>

      {status === 'UNDER_REVIEW' && (
        <ReleasePaymentButton
          selectedPaymentIds={selectedPaymentIds}
          count={selectedPaymentIds.length || paymentsCount}
          onSuccess={() => {
            refetch()
          }}
        />
      )}

      {status === 'OPEN' && (
        <StartReviewButton
          selectedPaymentIds={selectedPaymentIds}
          count={selectedPaymentIds.length || paymentsCount}
          onSuccess={() => {
            refetch()
          }}
        />
      )}

      {(['ENQUEUED_FOR_RELEASE', 'FAILED', 'APPROVED'].includes(status) ||
        isFailedApi ||
        isFailedBank ||
        isFailedUnknown) && (
        <RequeuePaymentButton
          onSuccess={() => {
            refetch()
          }}
          selectedPaymentIds={selectedPaymentIds}
        />
      )}

      <Box sx={{ flex: 1, minHeight: 0 }}>
        <DataGrid
          apiRef={dataGridApiRef}
          rows={payments}
          columns={getColumns({ status, isFailedApi, isFailedBank, isFailedUnknown })}
          loading={isPaymentsLoading || isPaymentsFetching}
          rowCount={data?.listPayments?.count || 0}
          paginationMode="server"
          sortingMode="server"
          filterMode="server"
          filterDebounceMs={500}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          filterModel={filterModel}
          onFilterModelChange={setFilterModel}
          pageSizeOptions={[50, 100, 250, 500]}
          checkboxSelection={
            ['ENQUEUED_FOR_RELEASE', 'FAILED', 'APPROVED', 'OPEN', 'UNDER_REVIEW'].includes(status) ||
            isFailedApi ||
            isFailedBank ||
            isFailedUnknown
          }
          onRowSelectionModelChange={handleRowSelectionModelChange}
          disableRowSelectionOnClick
          disableVirtualization
          sx={{
            '& .MuiDataGrid-row:hover': {
              cursor: 'pointer',
            },
          }}
          density="compact"
          slots={{
            noRowsOverlay: () => <NoTableResults text={`There are no ${status} payments.`} />,
          }}
        />
      </Box>
    </Box>
  )
}
